import React from "react";
import { TextField, Button, Typography, Box, Collapse, Popover } from "@mui/material";
import { ChevronDown, ChevronRight, Search } from "lucide-react";

export interface TreeDropdownOption {
    id: string;
    label: string;
    children?: TreeDropdownOption[];
}

export interface TreeDropdownProps {
    onChange?: (selectedId: string | null, selectedOption: TreeDropdownOption | null) => void;
    value?: string | null;
    options: TreeDropdownOption[];
    placeholder?: string;
    disabled?: boolean;
    error?: boolean;
    helperText?: string;
    height?: number;
    width?: number | string;
    borderColor?: string;
    hoverBorderColor?: string;
    focusBorderColor?: string;
    placeholderColor?: string;
    selectedTextColor?: string;
    enableHighlightMode?: boolean;
    // Rendered under the option list (e.g. an "Add category" button); gets a
    // function that closes the dropdown.
    footer?: (close: () => void) => React.ReactNode;
}

const TreeDropdown: React.FC<TreeDropdownProps> = ({ 
    onChange, 
    options, 
    placeholder, 
    value,
    disabled = false,
    error = false,
    helperText,
    height = 40,
    width,
    borderColor,
    hoverBorderColor,
    focusBorderColor,
    placeholderColor,
    selectedTextColor,
    enableHighlightMode = false,
    footer,
}) => {
    const keyRef = React.useRef<string>(Math.random().toString(36).substring(2, 15));
    const [filterValue, setFilterValue] = React.useState<string>('');
    const [selectedId, setSelectedId] = React.useState<string | null>(value || null);
    const [highlightedId, setHighlightedId] = React.useState<string | null>(null);
    const textFieldRef = React.useRef<any>(null);
    const scrollContainerRef = React.useRef<HTMLDivElement>(null);
    
    // Scroll highlighted item into view when it changes
    React.useEffect(() => {
        if (enableHighlightMode && highlightedId) {
            const element = document.getElementById(`tree-option-${highlightedId}`);
            if (element && scrollContainerRef.current) {
                element.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            }
        }
    }, [highlightedId, enableHighlightMode]);
    const getParentNodeIds = React.useCallback((options: TreeDropdownOption[]): { [id: string]: boolean } => {
        const parentIds: { [id: string]: boolean } = {};
        
        const traverse = (nodes: TreeDropdownOption[]) => {
            for (const node of nodes) {
                if (node.children && node.children.length > 0) {
                    parentIds[node.id] = true;
                    traverse(node.children);
                }
            }
        };
        
        traverse(options);
        return parentIds;
    }, []);
    
    const [expandedIds, setExpandedIds] = React.useState<{ [id: string]: boolean }>(() => getParentNodeIds(options));
    const [open, setOpen] = React.useState<boolean>(false);
    
    // Update expanded state when options change
    React.useEffect(() => {
        setExpandedIds(getParentNodeIds(options));
    }, [options, getParentNodeIds]);

    React.useEffect(() => {
        setSelectedId(value || null);
    }, [value]);

    const key = keyRef.current;

    // Find option by ID
    const findOptionById = React.useCallback((options: TreeDropdownOption[], targetId: string): TreeDropdownOption | null => {
        for (const option of options) {
            if (option.id === targetId || String(option.id) === String(targetId)) {
                return option;
            }
            if (option.children) {
                const found = findOptionById(option.children, targetId);
                if (found) return found;
            }
        }
        return null;
    }, []);

    // Get all leaf options in tree order (for keyboard navigation)
    const getAllOptions = React.useCallback((options: TreeDropdownOption[]): TreeDropdownOption[] => {
        const allOpts: TreeDropdownOption[] = [];
        const traverse = (nodes: TreeDropdownOption[]) => {
            for (const node of nodes) {
                allOpts.push(node);
                if (node.children) {
                    traverse(node.children);
                }
            }
        };
        traverse(options);
        return allOpts;
    }, []);

    // Find first match in visible tree
    const findFirstMatch = React.useCallback((options: TreeDropdownOption[], searchText: string): TreeDropdownOption | null => {
        const lower = searchText.toLowerCase();
        let firstMatch: TreeDropdownOption | null = null;
        
        const traverse = (nodes: TreeDropdownOption[]) => {
            if (firstMatch) return;
            for (const node of nodes) {
                if (node.label.toLowerCase().includes(lower)) {
                    firstMatch = node;
                    return;
                }
                if (node.children) {
                    traverse(node.children);
                }
            }
        };
        
        traverse(options);
        return firstMatch;
    }, []);

    // Generate display text for selected value
    const getSelectedText = React.useMemo(() => {
        if (!selectedId) return placeholder || 'Select...';
        
        const selectedOption = findOptionById(options, selectedId);
        return selectedOption?.label || placeholder || 'Select...';
    }, [selectedId, options, placeholder, findOptionById]);

    const handleSelect = (option: TreeDropdownOption) => {
        if (enableHighlightMode) {
            // Apply selection on click in highlight mode
            setSelectedId(option.id);
            setOpen(false);
            setFilterValue('');
            setHighlightedId(null);
            onChange && onChange(option.id, option);
        } else {
            setSelectedId(option.id);
            setOpen(false);
            setFilterValue('');
            onChange && onChange(option.id, option);
        }
    };

    const handleToggleExpand = (id: string) => {
        setExpandedIds(prev => ({ ...prev, [id]: !prev[id] }));
    };

    const filterTree = (options: TreeDropdownOption[]): TreeDropdownOption[] => {
        if (!filterValue.trim()) return options;

        const lower = filterValue.toLowerCase();
        const filterFn = (opt: TreeDropdownOption): TreeDropdownOption | null => {
            if (opt.label.toLowerCase().includes(lower)) return opt;
            if (opt.children) {
                const filteredChildren = opt.children.map(filterFn).filter(Boolean) as TreeDropdownOption[];
                if (filteredChildren.length) return { ...opt, children: filteredChildren };
            }
            return null;
        };
        return options.map(filterFn).filter(Boolean) as TreeDropdownOption[];
    };

    // Handle keyboard navigation in highlight mode
    const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (!enableHighlightMode || !open) {
            return;
        }

        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            e.preventDefault();
            const allOpts = getAllOptions(options);
            if (allOpts.length === 0) return;
            
            let currentIndex = allOpts.findIndex(opt => opt.id === highlightedId);
            
            // If no item is highlighted, start from -1 for down or length for up
            if (currentIndex === -1) {
                currentIndex = e.key === 'ArrowDown' ? -1 : allOpts.length - 1;
            }
            
            let nextIndex = 0;
            if (e.key === 'ArrowDown') {
                nextIndex = currentIndex >= allOpts.length - 1 ? 0 : currentIndex + 1;
            } else {
                nextIndex = currentIndex <= 0 ? allOpts.length - 1 : currentIndex - 1;
            }
            
            const nextOption = allOpts[nextIndex];
            setHighlightedId(nextOption.id);
            
            // Auto-expand parents to show the highlighted item
            const expandParents = (opts: TreeDropdownOption[], targetId: string, isParent = false): boolean => {
                for (const opt of opts) {
                    if (opt.id === targetId) return isParent;
                    if (opt.children) {
                        if (expandParents(opt.children, targetId, true)) {
                            setExpandedIds(prev => ({ ...prev, [opt.id]: true }));
                            return true;
                        }
                    }
                }
                return false;
            };
            expandParents(options, nextOption.id);
        } else if (e.key === 'Enter') {
            e.preventDefault();
            if (highlightedId) {
                const selectedOption = findOptionById(options, highlightedId);
                if (selectedOption) {
                    setSelectedId(selectedOption.id);
                    setOpen(false);
                    setFilterValue('');
                    setHighlightedId(null);
                    onChange && onChange(selectedOption.id, selectedOption);
                }
            }
        }
    };

    const renderTree = (
        options: TreeDropdownOption[],
        level = 0
    ) => {
        return options.map((option) => {
            const hasChildren = option.children && option.children.length > 0;
            const expanded = !!expandedIds[option.id];
            const isSelected = selectedId === option.id || String(selectedId) === String(option.id);
            const isHighlighted = enableHighlightMode && (highlightedId === option.id || String(highlightedId) === String(option.id));

            return (
                <Box
                    id={`tree-option-${option.id}`}
                    key={option.id}
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        width
                    }}
                >
                    <Box 
                        display="flex" 
                        alignItems="center" 
                        sx={{ 
                            pl: `${level * 16}px`,
                            pr: 1,
                            py: 0.5,
                            cursor: 'pointer',
                            borderRadius: 1,
                            backgroundColor: isHighlighted ? 'rgba(var(--color-primary-rgb), 0.2078)' : (isSelected ? 'rgba(var(--color-primary-rgb), 0.0824)' : 'transparent'),
                            '&:hover': { 
                                backgroundColor: isHighlighted ? 'rgba(var(--color-primary-rgb), 0.2078)' : (isSelected ? 'rgba(var(--color-primary-rgb), 0.1451)' : 'var(--color-container-background)')
                            },
                            minHeight: 32,
                            boxSizing: 'border-box'
                        }}
                        onMouseDown={(e) => {
                            e.preventDefault();
                            handleSelect(option);
                        }}
                    >
                        {hasChildren ? (
                            <Button
                                size="small"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleToggleExpand(option.id);
                                }}
                                sx={{ 
                                    minWidth: 20, 
                                    width: 20,
                                    height: 20, 
                                    p: 0,
                                    mr: 0.5,
                                    color: 'var(--color-text-muted)'
                                }}
                            >
                                {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                            </Button>
                        ) : (
                            <Box sx={{ width: 20, mr: 0.5 }} /> // Spacer for alignment
                        )}
                        <Typography 
                            fontSize={13} 
                            sx={{ 
                                userSelect: "none",
                                fontWeight: isHighlighted ? 700 : (isSelected ? 600 : 400),
                                color: isHighlighted ? 'var(--color-primary)' : (isSelected ? 'var(--color-primary)' : 'inherit'),
                                flex: 1
                            }}
                        >
                            {option.label}
                        </Typography>
                    </Box>
                    {hasChildren && (
                        <Collapse in={expanded} timeout="auto" unmountOnExit>
                            <Box>
                                {renderTree(option.children!, level + 1)}
                            </Box>
                        </Collapse>
                    )}
                </Box>
            );
        });
    };

    return (
        <Box position="relative" display="inline-block" width="100%">
            <Box
                id={`tree-dropdown-anchor-${key}`}
                sx={{
                    fontSize: 14,
                    background: disabled ? 'var(--color-container-background)' : '#fff',
                    width: width || '100%',
                    height: height,
                    border: `1px solid ${borderColor || (error ? '#d32f2f' : '#ccc')}`,
                    borderRadius: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: 1.5,
                    cursor: disabled ? 'default' : 'pointer',
                    boxSizing: 'border-box',
                    '&:hover': {
                        borderColor: disabled ? '#ccc' : (hoverBorderColor || (error ? '#d32f2f' : 'var(--color-primary)'))
                    },
                    '&:focus-within': {
                        borderColor: disabled ? '#ccc' : (focusBorderColor || (error ? '#d32f2f' : 'var(--color-primary)')),
                        outline: `2px solid ${error ? '#d32f2f25' : 'rgba(var(--color-primary-rgb), 0.1451)'}`
                    }
                }}
                onClick={() => !disabled && setOpen(true)}
            >
                <Typography 
                    color={selectedId ? (selectedTextColor || 'black') : (placeholderColor || 'var(--color-text-muted)')} 
                    fontSize={14} 
                    textOverflow="ellipsis" 
                    overflow="hidden" 
                    whiteSpace="nowrap"
                    sx={{ flex: 1 }}
                >
                    {getSelectedText}
                </Typography>
                <ChevronDown 
                    size={18} 
                    color={disabled ? '#ccc' : 'var(--color-text-muted)'}
                    style={{ 
                        transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.2s ease'
                    }}
                />
            </Box>
            
            {helperText && (
                <Typography 
                    fontSize={12} 
                    color={error ? '#d32f2f' : 'var(--color-text-muted)'} 
                    sx={{ mt: 0.5, ml: 1.5 }}
                >
                    {helperText}
                </Typography>
            )}

            <Popover
                open={open && !disabled}
                anchorEl={document.getElementById(`tree-dropdown-anchor-${key}`)}
                onClose={() => {
                    setOpen(false);
                    setFilterValue('');
                    if (enableHighlightMode) setHighlightedId(null);
                }}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                PaperProps={{
                    sx: {
                        borderRadius: 2,
                        boxShadow: 3,
                        p: 1,
                        minWidth: 200,
                        maxWidth: 400,
                        width: width || 200,
                    },
                    onKeyDown: handleKeyDown,
                }}
            >
                <TextField
                    ref={textFieldRef}
                    fullWidth
                    size="small"
                    placeholder="Search categories..."
                    value={filterValue}
                    onChange={e => {
                        setFilterValue(e.target.value);
                        if (enableHighlightMode && e.target.value.trim()) {
                            const match = findFirstMatch(options, e.target.value);
                            if (match) {
                                setHighlightedId(match.id);
                                // Auto-expand parents
                                const expandParents = (opts: TreeDropdownOption[], targetId: string, isParent = false): boolean => {
                                    for (const opt of opts) {
                                        if (opt.id === targetId) return isParent;
                                        if (opt.children) {
                                            if (expandParents(opt.children, targetId, true)) {
                                                setExpandedIds(prev => ({ ...prev, [opt.id]: true }));
                                                return true;
                                            }
                                        }
                                    }
                                    return false;
                                };
                                expandParents(options, match.id);
                            }
                        }
                    }}
                    autoFocus
                    sx={{ mb: 1, mt: 0.5 }}
                    InputProps={{
                        sx: { fontSize: 13 },
                        startAdornment: <Search size={16} color="var(--color-primary)" style={{ marginRight: 8 }} />,
                    }}
                />
                <Box ref={scrollContainerRef} sx={{ maxHeight: 300, overflowY: "auto", mb: 0.5 }}>
                    {/* Built only while open - a table renders one of these per
                        row, and building every closed one's whole category tree
                        on each render was a large part of the Transactions
                        page's render time. */}
                    {open && (() => {
                        const visible = filterTree(options);
                        return visible.length > 0 ? renderTree(visible) : (
                            <Typography fontSize={13} color="var(--color-text-muted)" sx={{ p: 2, textAlign: 'center' }}>
                                No categories found
                            </Typography>
                        );
                    })()}
                </Box>
                {footer && (
                    <Box sx={{ borderTop: '1px solid rgba(0,0,0,0.08)', pt: 0.5 }}>
                        {footer(() => { setOpen(false); setFilterValue(''); })}
                    </Box>
                )}
            </Popover>
        </Box>
    );
};

export default TreeDropdown;